import { useCallback, useEffect, useRef, useState } from "react";
import {
  createTicketChatSocket,
  joinTicketChatRoom,
  leaveTicketChatRoom,
  sendTicketChatMessage,
} from "../utils/ticketChatSocket";

const useTicketChatSocket = ({
  companyId,
  supportTicketId,
  token,
  enabled = true,
  onMessage,
  onJoined,
  onError,
}) => {
  const socketRef = useRef(null);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (!enabled || !companyId || !supportTicketId || !token) return undefined;

    const socket = createTicketChatSocket(token);
    socketRef.current = socket;

    const handleConnect = () => {
      setConnected(true);
      joinTicketChatRoom(socket, companyId, supportTicketId);
    };

    const handleDisconnect = () => {
      setConnected(false);
    };

    const handleJoined = (payload) => {
      if (typeof onJoined === "function") onJoined(payload);
    };

    const handleMessage = (payload) => {
      if (typeof onMessage === "function") onMessage(payload);
    };

    const handleError = (payload) => {
      if (typeof onError === "function") onError(payload);
    };

    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);
    socket.on("ticket:joined", handleJoined);
    socket.on("ticket:message", handleMessage);
    socket.on("ticket:error", handleError);

    return () => {
      leaveTicketChatRoom(socket, companyId, supportTicketId);
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
      socket.off("ticket:joined", handleJoined);
      socket.off("ticket:message", handleMessage);
      socket.off("ticket:error", handleError);
      socket.disconnect();
      socketRef.current = null;
      setConnected(false);
    };
  }, [companyId, enabled, onError, onJoined, onMessage, supportTicketId, token]);

  const sendMessage = useCallback(
    (message, messageType = "text") => {
      return sendTicketChatMessage(
        socketRef.current,
        companyId,
        supportTicketId,
        message,
        messageType
      );
    },
    [companyId, supportTicketId]
  );

  return { connected, sendMessage };
};

export default useTicketChatSocket;
